"use client";

import { useEffect } from "react";
import useAuth from "~/services/auth/use-auth";
import { useAuthGetIntercomTokenService } from "~/services/api/services/auth";
import HTTP_CODES_ENUM from "~/services/api/types/http-codes";
import { AppConfig } from "~/utils/AppConfig";
import useUserId from "~/hooks/useUserId";
import { useLogEvent } from "~/hooks/useLogEvent";
import { getStorageItem } from "~/lib/storage/Storage";
import { STORAGE_KEYS } from "~/types/Storage";

const APP_ID = AppConfig.isProduction ? "pgcayrua" : "qaek6fb1";

export const trackIntercomEvent = (event_name) => {
    window?.Intercom?.("trackEvent", event_name);
};

export const shutdown = () => {
    window?.Intercom?.("shutdown");
};

export const openChat = () => {
    window?.Intercom?.("show");
};

export const hideChat = () => {
    window?.Intercom?.("update", {
        "hide_default_launcher": true
    });
};

export const showChat = () => {
    window?.Intercom?.("update", {
        "hide_default_launcher": false
    });
};

const IntercomScript = () => {
    const { user, isLoaded } = useAuth();
    const fetchAuthGetIntercomToken = useAuthGetIntercomTokenService();
    const { userId, isLoading: isUserIdLoading } = useUserId();
    const logEvent = useLogEvent();

    useEffect(() => {
        if (isLoaded && !isUserIdLoading) {
            if (!userId) {
                logEvent({
                    message: "[IntercomScript] userId is null.",
                    tag: "userId-action",
                });
                return;
            }

            const loadIntercom = () => {
                const myListener = async () => {
                    document.removeEventListener("pointermove", myListener, false);

                    if (getStorageItem(STORAGE_KEYS.IS_ADMIN_LOGIN)) {
                        return;
                    }

                    const { data, status } = await fetchAuthGetIntercomToken({
                        userId: userId || "",
                    });

                    if (status === HTTP_CODES_ENUM.OK) {
                        import("@intercom/messenger-js-sdk")
                            .then((mod) => {
                                mod.shutdown();

                                mod.Intercom({
                                    app_id: APP_ID,
                                    intercom_user_jwt: data?.intercomChatToken,
                                });

                                if (
                                    window.location.pathname.includes("/quiz") ||
                                    window.location.pathname.includes("webinar")
                                ) {
                                    hideChat();
                                }

                                if (window.location.search.includes("openChat=true")) {
                                    openChat();
                                }

                                dispatchEvent( new CustomEvent("ib/intercom/loaded") );
                            })
                            .catch((err) => {
                                console.error("Failed to load module", err);
                            });
                    }
                };
            
                document.addEventListener("pointermove", myListener, false);
            };

            loadIntercom();
        }
    }, [user, isLoaded, fetchAuthGetIntercomToken, isUserIdLoading, userId, logEvent]);

    return null;
};

export default IntercomScript;
